import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, CreditCard, Truck, Shield, Loader2 } from 'lucide-react';

interface Medication {
  id: string;
  medication: string;
  dose: string;
  quantity_authorized: string;
  unit_of_measure: string;
  sig: string;
  refills_authorized: string;
  price: number;
  product_id: string;
}

interface CartSummaryProps {
  selectedMedications: Medication[];
  cartTotal: number;
  taxAmount: number;
  finalTotal: number;
  onProceedToPayment: () => void;
  isUpdating?: boolean;
  disabled?: boolean;
  programServiceFee?: number;      // in cents
  clinicFacilitationFee?: number;  // in cents
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const CartSummary: React.FC<CartSummaryProps> = ({
  selectedMedications,
  cartTotal,
  taxAmount,
  finalTotal,
  onProceedToPayment,
  isUpdating = false,
  disabled = false,
  programServiceFee,
  clinicFacilitationFee,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      <Card className="shadow-lg border-gray-200">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Order Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Selected Items */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Selected Items ({selectedMedications.length})
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedMedications.length > 0 ? (
                selectedMedications.map((med) => (
                  <div key={med.id} className="flex justify-between text-sm">
                    <div className="flex-1 pr-2">
                      <p className="font-medium truncate">{med.medication}</p>
                      <p className="text-xs text-gray-500">{med.dose}</p>
                    </div>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(med.price)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No items selected</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Fee Breakdown */}
          <div className="space-y-2">
            {programServiceFee !== undefined && clinicFacilitationFee !== undefined ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Program Service Fee</span>
                  <span className="font-medium">
                    {isUpdating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      formatCurrency(programServiceFee / 100)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Clinic Facilitation Fee</span>
                  <span className="font-medium">
                    {isUpdating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      formatCurrency(clinicFacilitationFee / 100)
                    )}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Consulting Fees</span>
                <span className="font-medium">
                  {isUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    formatCurrency(cartTotal)
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax (8.25%)</span>
              <span className="font-medium">
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  formatCurrency(taxAmount)
                )}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Shipping</span>
              <Badge variant="secondary" className="text-xs">
                FREE
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Total */}
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-2xl font-bold text-blue-600">
              {isUpdating ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                formatCurrency(finalTotal)
              )}
            </span>
          </div>

          {/* Checkout Button */}
          <Button
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={onProceedToPayment}
            disabled={disabled || isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Updating Cart...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-5 w-5" />
                Proceed to Payment
              </>
            )}
          </Button>

          {/* Trust Badges */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Shield className="h-4 w-4 text-green-600" />
              <span>Secure checkout powered by Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Truck className="h-4 w-4 text-blue-600" />
              <span>Free shipping on all orders</span>
            </div>
          </div>

          {/* Savings Badge */}
          {selectedMedications.length > 2 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
            >
              <Badge className="w-full justify-center py-2 bg-green-100 text-green-800 border-green-300">
                You're saving with program enrollment!
              </Badge>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CartSummary; 